import { Link, NavLink, Outlet } from "react-router-dom";
import { Flame, Compass, Plus, MessagesSquare, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { mediaSrc } from "@/lib/api";
import UserMenu, { MessagesLink } from "@/components/UserMenu";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/", label: "Feed", icon: Flame, id: "nav-feed-link" },
  { to: "/explore", label: "Explore", icon: Compass, id: "nav-explore-link" },
  { to: "/c", label: "Communities", icon: MessagesSquare, id: "nav-communities-link" },
];

export const Logo = () => (
  <Link to="/" data-testid="logo-link" className="font-display text-lg font-bold !tracking-[0.3em] text-white">
    RIFF<span className="text-[#FF2E4D]">.</span>
  </Link>
);

export const Avatar = ({ user, size = "h-9 w-9", className = "" }) => (
  <div className={`${size} shrink-0 overflow-hidden rounded-full bg-[#1C1F26] ${className}`}>
    {user?.avatar_url ? (
      <img src={mediaSrc(user.avatar_url)} alt={user.name} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center font-display font-semibold text-white">
        {(user?.name || "?")[0].toUpperCase()}
      </div>
    )}
  </div>
);

const linkCls = ({ isActive }) =>
  `flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
  }`;

function TopNav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 h-14 border-b border-[#2A2E37] bg-[#090A0C]/80 backdrop-blur-xl" data-testid="top-nav">
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 md:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`desktop-${n.id}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/upload" data-testid="desktop-nav-upload-button"
            className="btn-neon hidden items-center gap-1.5 rounded-sm px-4 py-2 text-sm font-semibold text-white md:flex">
            <Plus className="h-4 w-4" /> Post
          </Link>
          {user ? (
            <>
              <MessagesLink />
              <NotificationsBell />
              <UserMenu />
            </>
          ) : user === false ? (
            <Link to="/login" data-testid="nav-login-link"
              className="rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Log in
            </Link>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const { user } = useAuth();
  const item = ({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-semibold ${isActive ? "text-white" : "text-gray-500"}`;
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t border-[#2A2E37] bg-black/70 backdrop-blur-xl md:hidden" data-testid="bottom-nav">
      <NavLink to="/" end className={item} data-testid="nav-feed-link"><Flame className="h-5 w-5" />Feed</NavLink>
      <NavLink to="/explore" className={item} data-testid="nav-explore-link"><Compass className="h-5 w-5" />Explore</NavLink>
      <Link to="/upload" data-testid="nav-upload-button" className="btn-neon flex h-10 w-14 items-center justify-center rounded-sm text-white">
        <Plus className="h-6 w-6" />
      </Link>
      <NavLink to="/c" className={item} data-testid="nav-communities-link"><MessagesSquare className="h-5 w-5" />Talk</NavLink>
      <NavLink to={user ? `/u/${user.username}` : "/login"} className={item} data-testid="nav-profile-link"><User className="h-5 w-5" />Me</NavLink>
    </nav>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-[#090A0C] text-gray-100">
      <TopNav />
      <main className="pb-16 md:pb-0"><Outlet /></main>
      <BottomNav />
    </div>
  );
}
